import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, ArrowLeft, Save } from "lucide-react";
import { Recipe, RecipeIngredient, RecipeStep, RECIPE_CATEGORIES, RECIPE_DIFFICULTIES, INGREDIENT_UNITS } from "@/models/cookbook";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";

// Temporary types for form state
interface FormStep extends Omit<RecipeStep, "id" | "recipe_id" | "created_at"> {
    id?: string; // Optional for new steps
    tempId: string; // For UI tracking
}

interface FormIngredient extends Omit<RecipeIngredient, "id" | "recipe_id" | "created_at"> {
    id?: string; // Optional for new ingredients
    tempId: string; // For UI tracking
    stepTempId?: string; // Link to step via tempId
}

const RecipeForm = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);

    // Recipe State
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [category, setCategory] = useState("");
    const [cookingTime, setCookingTime] = useState<number | "">("");
    const [difficulty, setDifficulty] = useState("");
    const [servings, setServings] = useState<number>(4);
    const [sourceUrl, setSourceUrl] = useState("");

    // Lists
    const [steps, setSteps] = useState<FormStep[]>([]);
    const [ingredients, setIngredients] = useState<FormIngredient[]>([]);

    // Image Upload State
    const [imageUploadMode, setImageUploadMode] = useState<"url" | "file">("url");
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        if (id) {
            fetchRecipe(id);
        }
    }, [id]);

    const fetchRecipe = async (recipeId: string) => {
        try {
            const { data: recipe, error: recipeError } = await supabase
                .from("recipes")
                .select("*")
                .eq("id", recipeId)
                .single();

            if (recipeError) throw recipeError;

            setTitle(recipe.title);
            setDescription(recipe.description || "");
            setImageUrl(recipe.image_url || "");
            setCategory(recipe.category || "");
            setCookingTime(recipe.cooking_time || "");
            setDifficulty(recipe.difficulty || "");
            setServings(recipe.servings || 4);
            setSourceUrl((recipe as any).source_url || "");

            const { data: stepsData, error: stepsError } = await supabase
                .from("recipe_steps")
                .select("*")
                .eq("recipe_id", recipeId)
                .order("order_index");

            if (stepsError) throw stepsError;

            const loadedSteps = (stepsData || []).map(s => ({
                ...s,
                tempId: s.id // Use real ID as tempId for existing
            }));
            setSteps(loadedSteps);

            const { data: ingredientsData, error: ingredientsError } = await supabase
                .from("recipe_ingredients")
                .select("*")
                .eq("recipe_id", recipeId)
                .order("order_index");

            if (ingredientsError) throw ingredientsError;

            setIngredients((ingredientsData || []).map(i => ({
                ...i,
                tempId: i.id, // Use real ID as tempId for existing
                stepTempId: i.step_id || undefined
            })));

        } catch (error) {
            console.error("Error fetching recipe:", error);
            toast({
                title: "Error",
                description: "Failed to load recipe details.",
                variant: "destructive",
            });
        } finally {
            setFetching(false);
        }
    };

    const handleAddStep = () => {
        setSteps([...steps, {
            description: "",
            order_index: steps.length + 1,
            tempId: `new-step-${Date.now()}`,
            recommendation: null
        }]);
    };

    const handleRemoveStep = (index: number) => {
        const stepToRemove = steps[index];
        // Also remove link from ingredients
        setIngredients(ingredients.map(i =>
            i.stepTempId === stepToRemove.tempId ? { ...i, stepTempId: undefined } : i
        ));
        setSteps(steps.filter((_, i) => i !== index));
    };

    const handleStepChange = (index: number, value: string) => {
        const newSteps = [...steps];
        newSteps[index].description = value;
        setSteps(newSteps);
    };

    const handleAddIngredient = (stepTempId: string) => {
        setIngredients([...ingredients, {
            name: "",
            quantity: 0,
            unit: "",
            order_index: ingredients.length + 1,
            tempId: `new-ing-${Date.now()}`,
            stepTempId,
            step_id: null
        }]);
    };

    const handleRemoveIngredient = (tempId: string) => {
        setIngredients(ingredients.filter(i => i.tempId !== tempId));
    };

    const handleIngredientChange = (tempId: string, field: keyof FormIngredient, value: any) => {
        setIngredients(ingredients.map(i =>
            i.tempId === tempId ? { ...i, [field]: value } : i
        ));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }

        const file = e.target.files[0];

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast({
                title: "Invalid file type",
                description: "Please upload a valid image file (JPG, PNG, WebP, or GIF)",
                variant: "destructive",
            });
            e.target.value = ''; // Reset the input
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            toast({
                title: "File too large",
                description: "Please upload an image smaller than 5MB",
                variant: "destructive",
            });
            e.target.value = ''; // Reset the input
            return;
        }

        const fileExt = file.name.split('.').pop()?.toLowerCase();
        const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const filePath = `pictures/${fileName}`;

        setUploadingImage(true);

        try {
            const { error: uploadError } = await supabase.storage
                .from('recipes')
                .upload(filePath, file);

            if (uploadError) {
                throw uploadError;
            }

            const { data } = supabase.storage
                .from('recipes')
                .getPublicUrl(filePath);

            setImageUrl(data.publicUrl);
            toast({
                title: "Success",
                description: "Image uploaded successfully",
            });
        } catch (error: any) {
            console.error('Error uploading image:', error);
            toast({
                title: "Error",
                description: "Failed to upload image: " + error.message,
                variant: "destructive",
            });
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const recipeData = {
                title,
                description,
                image_url: imageUrl,
                category,
                cooking_time: cookingTime === "" ? null : Number(cookingTime),
                difficulty,
                servings,
                source_url: sourceUrl
            };

            let recipeId = id;

            if (id) {
                // Update existing recipe
                const { error } = await supabase
                    .from("recipes")
                    .update(recipeData)
                    .eq("id", id);
                if (error) throw error;
            } else {
                // Create new recipe
                const { data, error } = await supabase
                    .from("recipes")
                    .insert(recipeData)
                    .select()
                    .single();
                if (error) throw error;
                recipeId = data.id;
            }

            if (!recipeId) throw new Error("No recipe ID");

            // Handle Steps
            // First, delete existing steps if updating (simplest strategy for reordering)
            // Ideally we'd update, but for now let's just clear and re-insert or update carefully.
            // Actually, deleting everything and re-inserting breaks foreign keys if not careful.
            // Better: Upsert steps.

            // For simplicity in this iteration:
            // 1. We need to map tempIds to real IDs after insertion to link ingredients.

            const stepIdMap: Record<string, string> = {};

            // Process steps one by one to ensure we get IDs back
            for (let i = 0; i < steps.length; i++) {
                const step = steps[i];
                const stepData = {
                    recipe_id: recipeId,
                    description: step.description,
                    order_index: i + 1,
                    recommendation: step.recommendation
                };

                if (step.id) {
                    // Update existing
                    await supabase.from("recipe_steps").update(stepData).eq("id", step.id);
                    stepIdMap[step.tempId] = step.id;
                } else {
                    // Insert new
                    const { data, error } = await supabase.from("recipe_steps").insert(stepData).select().single();
                    if (error) throw error;
                    stepIdMap[step.tempId] = data.id;
                }
            }

            // Handle Ingredients
            for (let i = 0; i < ingredients.length; i++) {
                const ing = ingredients[i];
                const ingData = {
                    recipe_id: recipeId,
                    name: ing.name,
                    quantity: Number(ing.quantity),
                    unit: ing.unit,
                    order_index: i + 1,
                    step_id: ing.stepTempId ? stepIdMap[ing.stepTempId] : null
                };

                if (ing.id) {
                    await supabase.from("recipe_ingredients").update(ingData).eq("id", ing.id);
                } else {
                    await supabase.from("recipe_ingredients").insert(ingData);
                }
            }

            // Handle deletions (steps/ingredients not in the lists anymore)
            // This part is tricky with the current logic.
            // For a robust solution, we should fetch existing IDs at start, compare with current list, and delete missing.
            // I'll skip complex deletion logic for this MVP step and assume manual cleanup or future enhancement.
            // Actually, let's do a quick cleanup if editing.
            if (id) {
                const currentStepIds = steps.map(s => s.id).filter(Boolean);
                if (currentStepIds.length > 0) {
                    await supabase.from("recipe_steps").delete().eq("recipe_id", id).not("id", "in", currentStepIds);
                }

                const currentIngIds = ingredients.map(i => i.id).filter(Boolean);
                if (currentIngIds.length > 0) {
                    await supabase.from("recipe_ingredients").delete().eq("recipe_id", id).not("id", "in", currentIngIds);
                }
            }

            toast({
                title: "Success",
                description: "Recipe saved successfully.",
            });
            navigate("/admin");

        } catch (error: any) {
            console.error("Error saving recipe:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to save recipe.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return <div className="p-8">Loading...</div>;
    }

    return (
        <div className="container mx-auto p-4 md:p-8 max-w-4xl">
            <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
            </Button>

            <h1 className="text-3xl font-bold mb-8">{id ? "Edit Recipe" : "Create New Recipe"}</h1>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Info */}
                <Card>
                    <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-2">
                            <Label htmlFor="title">Title</Label>
                            <Input id="title" value={title} onChange={e => setTitle(e.target.value)} required />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea id="description" value={description} onChange={e => setDescription(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="grid gap-2">
                                <Label htmlFor="category">Category</Label>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RECIPE_CATEGORIES.map(cat => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="difficulty">Difficulty</Label>
                                <Select value={difficulty} onValueChange={setDifficulty}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Difficulty" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RECIPE_DIFFICULTIES.map(diff => (
                                            <SelectItem key={diff} value={diff}>{diff}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="cookingTime">Cooking Time (min)</Label>
                                <Input id="cookingTime" type="number" value={cookingTime} onChange={e => setCookingTime(e.target.value === "" ? "" : Number(e.target.value))} />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="servings">Servings</Label>
                                <Input id="servings" type="number" value={servings} onChange={e => setServings(Number(e.target.value))} />
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label>Image Source</Label>
                            <RadioGroup
                                defaultValue="url"
                                value={imageUploadMode}
                                onValueChange={(value) => setImageUploadMode(value as "url" | "file")}
                                className="flex flex-row space-x-4 mb-2"
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="url" id="mode-url" />
                                    <Label htmlFor="mode-url">Image URL</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="file" id="mode-file" />
                                    <Label htmlFor="mode-file">Upload Image</Label>
                                </div>
                            </RadioGroup>

                            {imageUploadMode === "url" ? (
                                <Input
                                    id="imageUrl"
                                    value={imageUrl}
                                    onChange={e => setImageUrl(e.target.value)}
                                    placeholder="https://..."
                                />
                            ) : (
                                <div className="flex gap-2 items-center">
                                    <Input
                                        id="imageUpload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        disabled={uploadingImage}
                                    />
                                    {uploadingImage && <Loader2 className="h-4 w-4 animate-spin" />}
                                </div>
                            )}
                            {imageUrl && (
                                <div className="mt-2">
                                    <Label className="text-xs text-muted-foreground mb-1 block">Preview</Label>
                                    <div className="relative w-full max-w-sm aspect-video rounded-md overflow-hidden border bg-muted">
                                        <img src={imageUrl} alt="Preview" className="object-cover w-full h-full" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="sourceUrl">Source URL</Label>
                            <Input id="sourceUrl" value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} placeholder="https://example.com/recipe" />
                        </div>
                    </CardContent>
                </Card>

                {/* Steps & Ingredients */}
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-semibold">Preparation Steps</h2>
                        <Button type="button" onClick={handleAddStep} variant="outline">
                            <Plus className="mr-2 h-4 w-4" /> Add Step
                        </Button>
                    </div>



                    {/* Steps */}
                    {steps.map((step, index) => (
                        <Card key={step.tempId}>
                            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                                <CardTitle className="text-lg">Step {index + 1}</CardTitle>
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveStep(index)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Textarea
                                    placeholder="Describe this step..."
                                    value={step.description}
                                    onChange={e => handleStepChange(index, e.target.value)}
                                />

                                <div className="pl-4 border-l-2 border-muted">
                                    <h4 className="text-sm font-medium mb-2 text-muted-foreground">Ingredients for this step</h4>
                                    <div className="space-y-2">
                                        {ingredients.filter(i => i.stepTempId === step.tempId).map(ing => (
                                            <div key={ing.tempId} className="flex gap-2 items-center">
                                                <Input
                                                    placeholder="Qty"
                                                    type="number"
                                                    className="w-20"
                                                    value={ing.quantity || ""}
                                                    onChange={e => handleIngredientChange(ing.tempId, "quantity", e.target.value)}
                                                />
                                                <Select value={ing.unit || ""} onValueChange={val => handleIngredientChange(ing.tempId, "unit", val)}>
                                                    <SelectTrigger className="w-24">
                                                        <SelectValue placeholder="Unit" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {INGREDIENT_UNITS.map(unit => (
                                                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    placeholder="Ingredient Name"
                                                    className="flex-1"
                                                    value={ing.name}
                                                    onChange={e => handleIngredientChange(ing.tempId, "name", e.target.value)}
                                                />
                                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveIngredient(ing.tempId)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        ))}
                                        <Button type="button" variant="ghost" size="sm" onClick={() => handleAddIngredient(step.tempId)}>
                                            <Plus className="mr-2 h-3 w-3" /> Add Ingredient
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <div className="flex justify-end gap-4 sticky bottom-4 bg-background/80 backdrop-blur-sm p-4 border rounded-lg shadow-lg">
                    <Button type="button" variant="outline" onClick={() => navigate("/admin")}>Cancel</Button>
                    <Button type="submit" disabled={loading}>
                        <Save className="mr-2 h-4 w-4" />
                        {loading ? "Saving..." : "Save Recipe"}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default RecipeForm;
